import React, { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Sparkles, Layers } from 'lucide-react';
import CelebrityLightbox from './CelebrityLightbox';

const CelebrityCard = ({ celebrity, onClick }) => {
  const cover = celebrity.photos?.[0]?.image;
  const coverSrcset = celebrity.photos?.[0]?.image_srcset;
  const extraCount = (celebrity.photos?.length || 0) - 1;

  return (
    <button
      onClick={onClick}
      className="group relative shrink-0 w-40 sm:w-56 md:w-64 rounded-2xl overflow-hidden bg-gray-100 shadow-sm hover:shadow-xl transition-shadow duration-300 snap-start text-left"
      style={{ aspectRatio: '3/4' }}
    >
      {cover ? (
        <img
          src={cover}
          srcSet={coverSrcset || undefined}
          sizes="(min-width: 768px) 256px, (min-width: 640px) 224px, 160px"
          alt={celebrity.name}
          className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
          loading="lazy"
          decoding="async"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
          <Sparkles className="w-10 h-10 text-gray-300" />
        </div>
      )}

      {extraCount > 0 && (
        <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/50 backdrop-blur-sm text-white text-[10px] font-medium px-2 py-1 rounded-full">
          <Layers className="w-3 h-3" />
          +{extraCount}
        </div>
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />

      <div className="absolute inset-x-0 bottom-0 p-3">
        <p className="text-white text-sm font-semibold truncate drop-shadow-sm">{celebrity.name}</p>
      </div>
    </button>
  );
};

const LoveByCelebs = ({ celebrities }) => {
  const scrollerRef = useRef(null);
  const [activeCelebrity, setActiveCelebrity] = useState(null);

  if (!celebrities || celebrities.length === 0) return null;

  const scrollBy = (amount) => {
    scrollerRef.current?.scrollBy({ left: amount, behavior: 'smooth' });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-5 sm:mb-7">Loved by Celebs</h2>

      <div className="relative">
        {celebrities.length > 1 && (
          <>
            <button
              onClick={() => scrollBy(-320)}
              className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 rounded-full bg-white shadow-lg border border-gray-100 text-gray-600 hover:text-primary-600 transition-colors"
              aria-label="Scroll celebrities left"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scrollBy(320)}
              className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 rounded-full bg-white shadow-lg border border-gray-100 text-gray-600 hover:text-primary-600 transition-colors"
              aria-label="Scroll celebrities right"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}

        <div
          ref={scrollerRef}
          className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scroll-smooth [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: 'none' }}
        >
          {celebrities.map(celebrity => (
            <CelebrityCard key={celebrity.id} celebrity={celebrity} onClick={() => setActiveCelebrity(celebrity)} />
          ))}
        </div>
      </div>

      {activeCelebrity && (
        <CelebrityLightbox celebrity={activeCelebrity} onClose={() => setActiveCelebrity(null)} />
      )}
    </div>
  );
};

export default LoveByCelebs;
